// # Calendar Event Form Component
// Create and edit calendar events with full field support

'use client';

import React, { useState, useEffect } from 'react';
import type {
  CalendarEvent,
  CalendarEventFormData,
  CalendarEventType,
  CalendarEventVisibility,
  RecurrencePattern
} from '../../types/calendar';
import { EVENT_TYPE_COLORS } from '../../types/calendar';

interface CalendarEventFormProps {
  event?: CalendarEvent; // If provided, form is in edit mode
  onSubmit: (data: CalendarEventFormData) => void;
  onCancel: () => void;
  loading?: boolean;
  error?: string;
  departments?: Array<{ id: string; name: string }>;
  defaultDepartmentId?: string;
  defaultVisibility?: CalendarEventVisibility;
  defaultDate?: string; // Default date for new events (YYYY-MM-DD)
  defaultEndDate?: string; // Default end date when creating from range select
}

export function CalendarEventForm({
  event,
  onSubmit,
  onCancel,
  loading = false,
  error,
  departments = [],
  defaultDepartmentId,
  defaultVisibility = 'private',
  defaultDate,
  defaultEndDate,
}: CalendarEventFormProps) {
  const initialStart = event?.start_date || defaultDate || new Date().toISOString().split('T')[0];
  const initialEnd =
    event?.end_date || defaultEndDate || defaultDate || new Date().toISOString().split('T')[0];
  // # Form state
  const [formData, setFormData] = useState<CalendarEventFormData>({
    title: event?.title || '',
    description: event?.description || '',
    notes: event?.notes || '',
    start_date: initialStart,
    end_date: initialEnd,
    start_time: event?.start_time || '',
    end_time: event?.end_time || '',
    all_day: event?.all_day ?? false,
    timezone: event?.timezone || 'UTC',
    event_type: event?.event_type || 'other',
    visibility: event?.visibility || defaultVisibility,
    department_id: event?.department_id || defaultDepartmentId,
    is_recurring: event?.is_recurring ?? false,
    recurrence_pattern: event?.recurrence_pattern,
    location: event?.location || '',
    meeting_url: event?.meeting_url || '',
    color: event?.color || EVENT_TYPE_COLORS.other,
    linked_task_id: event?.linked_task_id,
    linked_reminder_id: event?.linked_reminder_id
  });

  const [showRecurrence, setShowRecurrence] = useState(event?.is_recurring ?? false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // # Update end date when start date changes (if end is before start)
  useEffect(() => {
    if (formData.start_date > formData.end_date) {
      setFormData(prev => ({ ...prev, end_date: formData.start_date }));
    }
  }, [formData.start_date]);

  // # Update color when event type changes
  useEffect(() => {
    if (!event?.color) {
      setFormData(prev => ({
        ...prev,
        color: EVENT_TYPE_COLORS[formData.event_type]
      }));
    }
  }, [formData.event_type, event?.color]);

  // # Handle form field changes
  const handleChange = (field: keyof CalendarEventFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  // # Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }

    if (!formData.start_date) {
      errors.start_date = 'Start date is required';
    }

    if (!formData.end_date) {
      errors.end_date = 'End date is required';
    }

    if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
      errors.end_date = 'End date must be after start date';
    }

    if (!formData.all_day && formData.start_time && formData.end_time) {
      if (formData.start_date === formData.end_date && formData.start_time > formData.end_time) {
        errors.end_time = 'End time must be after start time';
      }
    }

    if (formData.visibility === 'department' && !formData.department_id) {
      errors.department_id = 'Department is required for department events';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // # Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Title */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
          Event Title *
        </label>
        <input
          type="text"
          id="title"
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            validationErrors.title ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Enter event title"
          required
        />
        {validationErrors.title && (
          <p className="mt-1 text-sm text-red-600">{validationErrors.title}</p>
        )}
      </div>

      {/* Event Type and Visibility */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="event_type" className="block text-sm font-medium text-gray-700 mb-1">
            Event Type *
          </label>
          <select
            id="event_type"
            value={formData.event_type}
            onChange={(e) => handleChange('event_type', e.target.value as CalendarEventType)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="single_day">Single Day</option>
            <option value="multi_day">Multi-Day</option>
            <option value="deadline">Deadline</option>
            <option value="milestone">Milestone</option>
            <option value="project">Project</option>
            <option value="meeting">Meeting</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label htmlFor="visibility" className="block text-sm font-medium text-gray-700 mb-1">
            Visibility *
          </label>
          <select
            id="visibility"
            value={formData.visibility}
            onChange={(e) => handleChange('visibility', e.target.value as CalendarEventVisibility)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="private">Private (only me)</option>
            <option value="department">Department</option>
            <option value="global">Global (everyone)</option>
          </select>
        </div>
      </div>

      {/* Department (if visibility is department) */}
      {formData.visibility === 'department' && departments.length > 0 && (
        <div>
          <label htmlFor="department_id" className="block text-sm font-medium text-gray-700 mb-1">
            Department *
          </label>
          <select
            id="department_id"
            value={formData.department_id || ''}
            onChange={(e) => handleChange('department_id', e.target.value || undefined)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              validationErrors.department_id ? 'border-red-300' : 'border-gray-300'
            }`}
            required
          >
            <option value="">Select department</option>
            {departments.map(dept => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>
          {validationErrors.department_id && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.department_id}</p>
          )}
        </div>
      )}

      {/* Date and Time */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="all_day"
            checked={formData.all_day}
            onChange={(e) => handleChange('all_day', e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="all_day" className="text-sm font-medium text-gray-700">
            All-day event
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Start Date/Time */}
          <div>
            <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date *
            </label>
            <input
              type="date"
              id="start_date"
              value={formData.start_date}
              onChange={(e) => handleChange('start_date', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                validationErrors.start_date ? 'border-red-300' : 'border-gray-300'
              }`}
              required
            />
            {validationErrors.start_date && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.start_date}</p>
            )}
            
            {!formData.all_day && (
              <>
                <label htmlFor="start_time" className="block text-sm font-medium text-gray-700 mt-2 mb-1">
                  Start Time
                </label>
                <input
                  type="time"
                  id="start_time"
                  value={formData.start_time}
                  onChange={(e) => handleChange('start_time', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </>
            )}
          </div>

          {/* End Date/Time */}
          <div>
            <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-1">
              End Date *
            </label>
            <input
              type="date"
              id="end_date"
              value={formData.end_date}
              onChange={(e) => handleChange('end_date', e.target.value)}
              min={formData.start_date}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                validationErrors.end_date ? 'border-red-300' : 'border-gray-300'
              }`}
              required
            />
            {validationErrors.end_date && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.end_date}</p>
            )}
            
            {!formData.all_day && (
              <>
                <label htmlFor="end_time" className="block text-sm font-medium text-gray-700 mt-2 mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  id="end_time"
                  value={formData.end_time}
                  onChange={(e) => handleChange('end_time', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors.end_time ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {validationErrors.end_time && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.end_time}</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Add event description"
        />
      </div>

      {/* Location and Meeting URL */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
            Location
          </label>
          <input
            type="text"
            id="location"
            value={formData.location}
            onChange={(e) => handleChange('location', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Add location"
          />
        </div>

        <div>
          <label htmlFor="meeting_url" className="block text-sm font-medium text-gray-700 mb-1">
            Meeting URL
          </label>
          <input
            type="url"
            id="meeting_url"
            value={formData.meeting_url}
            onChange={(e) => handleChange('meeting_url', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="https://..."
          />
        </div>
      </div>

      {/* Recurring Event Toggle */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="is_recurring"
          checked={showRecurrence}
          onChange={(e) => {
            setShowRecurrence(e.target.checked);
            handleChange('is_recurring', e.target.checked);
            if (!e.target.checked) {
              handleChange('recurrence_pattern', undefined);
            }
          }}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <label htmlFor="is_recurring" className="text-sm font-medium text-gray-700">
          Recurring event
        </label>
      </div>

      {/* Recurrence Pattern (simplified - full editor would be in RecurrencePatternEditor) */}
      {showRecurrence && (
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600 mb-2">
            Note: Advanced recurrence patterns can be configured after creating the event.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Frequency
              </label>
              <select
                value={formData.recurrence_pattern?.frequency || 'daily'}
                onChange={(e) => handleChange('recurrence_pattern', {
                  ...formData.recurrence_pattern,
                  frequency: e.target.value as any,
                  interval: formData.recurrence_pattern?.interval || 1
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Repeat every
              </label>
              <input
                type="number"
                min="1"
                value={formData.recurrence_pattern?.interval || 1}
                onChange={(e) => handleChange('recurrence_pattern', {
                  ...formData.recurrence_pattern,
                  frequency: formData.recurrence_pattern?.frequency || 'daily',
                  interval: parseInt(e.target.value) || 1
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Form Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Saving...' : event ? 'Update Event' : 'Create Event'}
        </button>
      </div>
    </form>
  );
}

export default CalendarEventForm;

